import { createMiddleware } from "@tanstack/react-start";
import { UnauthorizedError } from "@/lib/errors";
import type { ResolvedSession } from "@/server/use-cases/auth/resolve-session";

// Server-only modules are pulled in dynamically so TanStack Start can strip
// them from the client bundle. Static imports of @/server/* from this
// client-importable file would drag node:crypto (via the HMAC client) into
// the browser bundle. See client/docs/architecture.md §"The dependency rule".

// The one place the session is resolved. Registered globally in start.ts's
// requestMiddleware (once per HTTP request) AND composable into function
// middleware / server functions: Start tracks which request middlewares
// already ran for the current request and skips them in function chains, so
// composing it never resolves twice — while an invocation outside a request
// (a background job, a startup probe) still resolves for itself.
export const sessionRequestMiddleware = createMiddleware().server(async ({ next }) => {
  const { readSessionCookie } = await import("@/server/use-cases/auth/cookies");
  const { safeResolveSession } = await import("@/server/use-cases/auth/resolve-session");
  const session = await safeResolveSession(readSessionCookie());
  return next({ context: { session } });
});

export const requireAuth = createMiddleware({ type: "function" })
  .middleware([sessionRequestMiddleware])
  .server(async ({ next, context }) => {
    if (!context.session) throw new UnauthorizedError();
    const session: ResolvedSession = context.session;
    return next({ context: { session } });
  });
